import { apiFetchWithRetry } from './apiFetch';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Read a File/Blob as base64 (no data: prefix). readAsDataURL is the only
// built-in path to base64 and handles UTF-8 bytes correctly.
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export const studentService = {
  getCurrentUserProfile: async () => {
    try {
      const response = await apiFetchWithRetry(`/v1/students/profile/me`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch current user profile");
      }

      return data;
    } catch (error) {
      console.error("Error fetching current user profile:", error);
      throw error;
    }
  },

  // Preview invite token (public endpoint)
  previewInvite: async (inviteToken) => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/invites/${inviteToken}/preview`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to preview invite");
      }

      return data;
    } catch (error) {
      console.error("Error previewing invite:", error);
      throw error;
    }
  },

  // Accept invite token (authenticated endpoint)
  acceptInvite: async (inviteToken) => {
    try {
      const response = await apiFetchWithRetry(`/v1/invites/${inviteToken}/accept`, {
        method: "POST",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to accept invite");
      }

      return data;
    } catch (error) {
      console.error("Error accepting invite:", error);
      throw error;
    }
  },

  // Fetch students (active/inactive)
  getStudents: async ({ limit = 10, page = 1, search = "", status = "" }) => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        page: page.toString(),
      });

      if (search) {
        params.append("search", search);
      }

      if (status && status !== "all") {
        // Backend UserStatus enum is uppercase (ACTIVE / INACTIVE); the filter
        // dropdown values are lowercase, so normalize before sending.
        params.append("status", status.toUpperCase());
      }

      const response = await apiFetchWithRetry(`/v1/students?${params.toString()}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch students");
      }

      return data;
    } catch (error) {
      console.error("Error fetching students:", error);
      throw error;
    }
  },

  // Per-student engagement rollup for the Engagement page. Server-side
  // filter/search/sort/paginate (school-scoped). `filter` is one of:
  // all | yet_to_start | no_resume | no_hypepage | not_verified.
  getStudentEngagement: async ({ limit = 10, page = 1, search = "", filter = "all", sortBy = "", sortDir = "" } = {}) => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        page: page.toString(),
      });
      if (search) params.append("search", search);
      if (filter && filter !== "all") params.append("filter", filter);
      if (sortBy) params.append("sortBy", sortBy);
      if (sortDir) params.append("sortDir", sortDir);

      const response = await apiFetchWithRetry(`/v1/students/engagement?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch engagement");
      }
      return data;
    } catch (error) {
      console.error("Error fetching student engagement:", error);
      throw error;
    }
  },

  // School branding settings (logo + name). Read is open to any school
  // member; the PATCH is admin-tier only (enforced server-side).
  getSchoolSettings: async () => {
    try {
      const response = await apiFetchWithRetry(`/v1/students/school-settings`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to load settings");
      return data;
    } catch (error) {
      console.error("Error loading school settings:", error);
      throw error;
    }
  },

  updateSchoolSettings: async (payload) => {
    try {
      const response = await apiFetchWithRetry(`/v1/students/school-settings`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to save settings");
      return data;
    } catch (error) {
      console.error("Error saving school settings:", error);
      throw error;
    }
  },

  // Upload a school logo: presign → PUT the bytes straight to S3 → return
  // { url, key } for persisting via updateSchoolSettings. The PUT is a raw
  // fetch to the presigned S3 URL (no auth header / API base).
  // Upload the logo through the API (server-side to R2) — avoids a browser→R2
  // presigned PUT, which the R2 bucket CORS blocks from this origin. The image
  // is sent as base64; the server stores it and persists logoUrl/logoKey.
  // contentType is passed explicitly (derived from the extension by the
  // caller), not trusting file.type. Returns the updated SchoolSettingsDto.
  uploadSchoolLogo: async (file, contentType) => {
    const ct = contentType || file.type || "application/octet-stream";
    const base64Data = await fileToBase64(file);
    return studentService.saveSchoolLogo({ base64Data, contentType: ct, filename: file.name });
  },

  // Persist already-base64'd logo bytes (used by the manual upload and the
  // "import from website" confirm step). Returns the updated settings.
  saveSchoolLogo: async ({ base64Data, contentType, filename }) => {
    const response = await apiFetchWithRetry(`/v1/students/school-logo`, {
      method: "POST",
      body: JSON.stringify({ base64Data, contentType, filename: filename || "logo" }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Logo upload failed");
    return data;
  },

  // Extract a candidate logo from a school website URL. Returns
  // { base64Data, contentType, sourceUrl } for preview — does NOT save.
  extractLogoFromUrl: async (url) => {
    const response = await apiFetchWithRetry(`/v1/students/school-logo/extract`, {
      method: "POST",
      body: JSON.stringify({ url }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Could not extract a logo from that URL");
    return data;
  },

  // Upload a certificate signature image (server-side to R2, like the logo).
  // contentType is derived from the extension by the caller. Returns the
  // updated SchoolSettingsDto.
  uploadSchoolSignature: async (file, contentType) => {
    const ct = contentType || file.type || "application/octet-stream";
    const base64Data = await fileToBase64(file);
    const response = await apiFetchWithRetry(`/v1/students/school-signature`, {
      method: "POST",
      body: JSON.stringify({ base64Data, contentType: ct, filename: file.name }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Signature upload failed");
    return data;
  },

  // Save a signature the admin DREW or TYPED — it's already a transparent PNG
  // (base64), so we skip the File/extension step and hit the same endpoint as
  // the upload path. Returns the updated SchoolSettingsDto.
  saveSignatureImage: async (base64Data) => {
    const response = await apiFetchWithRetry(`/v1/students/school-signature`, {
      method: "POST",
      body: JSON.stringify({ base64Data, contentType: "image/png", filename: "signature.png" }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Couldn't save the signature");
    return data;
  },

  // Nudge students who haven't started — sends the "add your first
  // achievement" reminder email. Server scopes to the caller's school.
  // Returns { sent, skipped }.
  remindStudents: async (studentIds) => {
    try {
      const response = await apiFetchWithRetry(`/v1/students/remind`, {
        method: "POST",
        body: JSON.stringify({ studentIds }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to send reminders");
      }
      return data;
    } catch (error) {
      console.error("Error reminding students:", error);
      throw error;
    }
  },

  // Fetch pending invites
  getInvites: async ({ limit, page, search = "", userRole, excludeAccepted } = {}) => {
    try {
      const params = new URLSearchParams();

      if (limit !== undefined) params.append("limit", limit.toString());
      if (page !== undefined) params.append("page", page.toString());
      if (search) {
        params.append("search", search);
      }

      if (userRole) {
        params.append("userRole", userRole);
      }

      if (excludeAccepted) {
        params.append("excludeAccepted", "true");
      }

      const response = await apiFetchWithRetry(`/v1/invites?${params.toString()}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch invites");
      }

      return data;
    } catch (error) {
      console.error("Error fetching invites:", error);
      throw error;
    }
  },

  // Invite students. Second arg is optional; when provided with
  // expectedGraduationYear, every invite carries that year (used by
  // the cohort-import flow so future cohort-cert issuance works
  // immediately after acceptance).
  // `recipients` accepts either shape:
  //   ["a@school.edu", ...]                                  → sent as `emails`
  //   [{ email, firstName, lastName }, ...]                  → sent as `students`
  // The object form lets the invite email greet the student by first name
  // ("Hi Karen") instead of falling back to their raw address.
  inviteStudents: async (recipients, options = {}) => {
    try {
      const list = Array.isArray(recipients) ? recipients.filter(Boolean) : [];
      const withNames = list.some((r) => r && typeof r === "object");
      const body = withNames
        ? {
            students: list.map((r) =>
              typeof r === "string"
                ? { email: r }
                : { email: r.email, firstName: r.firstName || undefined, lastName: r.lastName || undefined },
            ),
          }
        : { emails: list };
      if (options.expectedGraduationYear) {
        body.expectedGraduationYear = options.expectedGraduationYear;
      }
      // Import-only: create pending invites without emailing (send later from
      // the Pending Students screen). Defaults to sending when omitted.
      if (options.sendEmails === false) {
        body.sendEmails = false;
      }
      const response = await apiFetchWithRetry(`/v1/invites/schools/students`, {
        method: "POST",
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to invite students");
      }

      return data;
    } catch (error) {
      console.error("Error inviting students:", error);
      throw error;
    }
  },

  // Bulk-send (or resend) invite emails for pending student invites by id —
  // powers "Send invites" on the Pending Students screen (incl. import-only
  // rows that were never emailed). Returns { sent }.
  sendStudentInvites: async (inviteIds) => {
    try {
      const response = await apiFetchWithRetry(`/v1/invites/schools/students/send`, {
        method: "POST",
        body: JSON.stringify({ inviteIds }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to send invites");
      }
      return data;
    } catch (error) {
      console.error("Error sending student invites:", error);
      throw error;
    }
  },

  // Permanently remove non-accepted student invites by id (roster mistake
  // cleanup) — the Pending Students bulk "Remove". Returns { deleted }.
  deleteStudentInvites: async (inviteIds) => {
    try {
      const response = await apiFetchWithRetry(`/v1/invites/schools/students/delete`, {
        method: "POST",
        body: JSON.stringify({ inviteIds }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to remove invites");
      }
      return data;
    } catch (error) {
      console.error("Error removing student invites:", error);
      throw error;
    }
  },

  /**
   * Extract a student roster from an uploaded file via the AI extractor.
   * The file must be base64-encoded by the caller (FileReader.readAsDataURL
   * or similar). Returns { students: [...], warnings: [...] }.
   *
   * 7MB raw file cap (~9.3MB base64) — matches the BE's
   * ROSTER_FILE_MAX_BYTES which sits under Fastify's 10MB body limit.
   */
  extractRoster: async ({ base64Data, mimeType, filename }) => {
    try {
      const response = await apiFetchWithRetry(`/v1/admin/roster/extract`, {
        method: "POST",
        body: JSON.stringify({ base64Data, mimeType, filename }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to extract roster");
      }
      return data;
    } catch (error) {
      console.error("Error extracting roster:", error);
      throw error;
    }
  },

  inviteSchoolStaff: async ({ email, role, name, title }) => {
    try {
      const response = await apiFetchWithRetry(`/v1/invites/schools/staff`, {
        method: "POST",
        body: JSON.stringify({ email, role, name, title }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to invite school staff");
      }

      return data;
    } catch (error) {
      console.error("Error inviting school staff:", error);
      throw error;
    }
  },

  getSchoolMemberships: async () => {
    try {
      const response = await apiFetchWithRetry(`/v1/invites/schools/staff`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch school memberships");
      }

      return data;
    } catch (error) {
      console.error("Error fetching school memberships:", error);
      throw error;
    }
  },

  getPendingStaffInvites: async () => {
    try {
      const response = await apiFetchWithRetry(`/v1/invites/schools/staff/pending`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch pending staff invites");
      }

      return data;
    } catch (error) {
      console.error("Error fetching pending staff invites:", error);
      throw error;
    }
  },

  cancelStaffInvite: async (inviteId) => {
    try {
      const response = await apiFetchWithRetry(`/v1/invites/schools/staff/pending/${inviteId}/cancel`, {
        method: "POST",
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to cancel staff invite");
      }

      return data;
    } catch (error) {
      console.error("Error cancelling staff invite:", error);
      throw error;
    }
  },

  resendStaffInvite: async (inviteId) => {
    try {
      const response = await apiFetchWithRetry(`/v1/invites/schools/staff/pending/${inviteId}/resend`, {
        method: "POST",
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to resend staff invite");
      }

      return data;
    } catch (error) {
      console.error("Error resending staff invite:", error);
      throw error;
    }
  },

  updateSchoolMembershipRole: async (membershipId, role) => {
    try {
      const response = await apiFetchWithRetry(`/v1/invites/schools/staff/${membershipId}/role`, {
        method: "POST",
        body: JSON.stringify({ role }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update membership role");
      }

      return data;
    } catch (error) {
      console.error("Error updating membership role:", error);
      throw error;
    }
  },

  updateSchoolMembershipStatus: async (membershipId, status) => {
    try {
      const response = await apiFetchWithRetry(`/v1/invites/schools/staff/${membershipId}/status`, {
        method: "POST",
        body: JSON.stringify({ status }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update membership status");
      }

      return data;
    } catch (error) {
      console.error("Error updating membership status:", error);
      throw error;
    }
  },

  // Resend invite
  resendInvite: async (inviteId) => {
    try {
      const response = await apiFetchWithRetry(`/v1/invites/${inviteId}/resend`, {
        method: "POST",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to resend invite");
      }

      return data;
    } catch (error) {
      console.error("Error resending invite:", error);
      throw error;
    }
  },

  // Cancel a pending invite — invalidates the recipient's link. Server allows
  // the original inviter (this school admin) to cancel their school's invites.
  cancelInvite: async (inviteId) => {
    try {
      const response = await apiFetchWithRetry(`/v1/invites/${inviteId}/cancel`, {
        method: "POST",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to cancel invite");
      }

      return data;
    } catch (error) {
      console.error("Error cancelling invite:", error);
      throw error;
    }
  },

  // Edit a roster student's basic details (name + grade). Admin-tier,
  // school-scoped on the backend. `payload` is { firstName, lastName, grade }
  // — only the keys present are written.
  updateStudent: async (studentId, payload) => {
    try {
      const response = await apiFetchWithRetry(`/v1/students/${studentId}/profile`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update student");
      }

      return data;
    } catch (error) {
      console.error("Error updating student:", error);
      throw error;
    }
  },

  // Deactivate student
  deactivateStudent: async (studentId) => {
    try {
      const response = await apiFetchWithRetry(`/v1/students/${studentId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: "INACTIVE" }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to deactivate student");
      }

      return data;
    } catch (error) {
      console.error("Error deactivating student:", error);
      throw error;
    }
  },

  // Permanently delete a student (test/junk cleanup). ADMIN-tier, school-scoped
  // on the backend. Erases the account + data, frees the email. Irreversible.
  deleteStudent: async (studentId) => {
    try {
      const response = await apiFetchWithRetry(`/v1/students/${studentId}`, {
        method: "DELETE",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to delete student");
      }

      return data;
    } catch (error) {
      console.error("Error deleting student:", error);
      throw error;
    }
  },

  // Activate student
  activateStudent: async (studentId) => {
    try {
      const response = await apiFetchWithRetry(`/v1/students/${studentId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: "ACTIVE" }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to activate student");
      }

      return data;
    } catch (error) {
      console.error("Error activating student:", error);
      throw error;
    }
  },

  // Export students
  // Note: backend returns raw StudentListItemDto[] array; this wrapper normalises
  // to { data, message } so existing page code (response.data, response.message) works.
  exportStudents: async ({ search = "", status = "" }) => {
    try {
      const params = new URLSearchParams();

      // params sent but currently ignored by backend (exports all students)
      if (search) params.append("search", search);
      if (status && status !== "all") params.append("status", status);

      const response = await apiFetchWithRetry(`/v1/students/export?${params.toString()}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to export students");
      }

      // Backend returns StudentListItemDto[] directly
      const students = Array.isArray(data) ? data : (data.data || []);
      return {
        data: students,
        message: "Students exported successfully",
      };
    } catch (error) {
      console.error("Error exporting students:", error);
      throw error;
    }
  },

  // Get dashboard stats
  getDashboardStats: async () => {
    try {
      const response = await apiFetchWithRetry(`/v1/dashboard/schools/stats`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch dashboard stats");
      }

      return data;
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      throw error;
    }
  },

  getSchoolJoinInfo: async () => {
    try {
      const response = await apiFetchWithRetry(`/v1/students/school-join-info`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch school join info");
      }

      return data;
    } catch (error) {
      console.error("Error fetching school join info:", error);
      throw error;
    }
  },

  departStudent: async (studentId, reasonPreset, reasonCustom, overrideNoPersonalEmail = false) => {
    try {
      const response = await apiFetchWithRetry(`/v1/students/${studentId}/depart`, {
        method: "PATCH",
        body: JSON.stringify({ reasonPreset, reasonCustom, overrideNoPersonalEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to depart student");
      }

      return data;
    } catch (error) {
      console.error("Error departing student:", error);
      throw error;
    }
  },

  // Students who have departed this school (the StudentSchoolDeparture log),
  // with reason + date. Backs the "Departed Students" tab. Paginated/searchable
  // like getStudents; returns { data, meta }.
  getDepartedStudents: async ({ limit = 10, page = 1, search = "" }) => {
    try {
      const params = new URLSearchParams({ limit: limit.toString(), page: page.toString() });
      if (search) {
        params.append("search", search);
      }

      const response = await apiFetchWithRetry(`/v1/students/departed?${params.toString()}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch departed students");
      }

      return data;
    } catch (error) {
      console.error("Error fetching departed students:", error);
      throw error;
    }
  },
};
